import React, { useState, useEffect } from 'react';
import { TwoFactorAuth } from './TwoFactorAuth.js';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle } from 'lucide-react';

interface AdminAuthGuardProps {
  children: React.ReactNode;
}

export const AdminAuthGuard: React.FC<AdminAuthGuardProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [_is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    checkAuthStatus();
    
    const interval = setInterval(() => {
      checkSessionValidity();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const checkAuthStatus = () => {
    const twoFAEnabled = localStorage.getItem('beehive-2fa-enabled') === 'true';
    const lastAuth = localStorage.getItem('beehive-admin-auth-time');
    const sessionTimeout = 30 * 60 * 1000; // 30 minutes
    
    setIs2FAEnabled(twoFAEnabled);
    
    if (!twoFAEnabled) {
      setNeedsSetup(true);
      setLoading(false);
      return;
    }
    
    if (lastAuth) {
      const authTime = parseInt(lastAuth);
      const now = Date.now();
      
      if (now - authTime < sessionTimeout) {
        setIsAuthenticated(true);
      } else {
        setSessionExpired(true);
        localStorage.removeItem('beehive-admin-auth-time');
      }
    }
    
    setLoading(false);
  };

  const checkSessionValidity = () => {
    const lastAuth = localStorage.getItem('beehive-admin-auth-time');
    const sessionTimeout = 30 * 60 * 1000; // 30 minutes
    
    if (lastAuth) {
      const authTime = parseInt(lastAuth);
      const now = Date.now();
      
      if (now - authTime >= sessionTimeout) {
        setIsAuthenticated(false);
        setSessionExpired(true);
        localStorage.removeItem('beehive-admin-auth-time');
      }
    }
  };

  const handleSetupComplete = (secret: string, backupCodes: string[]) => {
    localStorage.setItem('beehive-2fa-secret', secret);
    localStorage.setItem('beehive-2fa-backup-codes', JSON.stringify(backupCodes));
    localStorage.setItem('beehive-2fa-enabled', 'true');
    setIs2FAEnabled(true);
    setNeedsSetup(false);
    setIsAuthenticated(true);
    localStorage.setItem('beehive-admin-auth-time', Date.now().toString());
    
    const securityEvent = {
      event: '2fa_setup_completed',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      ipAddress: 'localhost' // In production, this would come from the server
    };
    
    const existingEvents = JSON.parse(localStorage.getItem('beehive-security-events') || '[]');
    existingEvents.unshift(securityEvent);
    localStorage.setItem('beehive-security-events', JSON.stringify(existingEvents.slice(0, 50)));
  };

  const handleVerificationComplete = (isValid: boolean) => {
    if (isValid) {
      setIsAuthenticated(true);
      setSessionExpired(false);
      localStorage.setItem('beehive-admin-auth-time', Date.now().toString());
      
      const securityEvent = {
        event: 'admin_login_success',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        ipAddress: 'localhost'
      };
      
      const existingEvents = JSON.parse(localStorage.getItem('beehive-security-events') || '[]');
      existingEvents.unshift(securityEvent);
      localStorage.setItem('beehive-security-events', JSON.stringify(existingEvents.slice(0, 50)));
    } else {
      const securityEvent = {
        event: 'admin_login_failed',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        ipAddress: 'localhost'
      };
      
      const existingEvents = JSON.parse(localStorage.getItem('beehive-security-events') || '[]');
      existingEvents.unshift(securityEvent);
      localStorage.setItem('beehive-security-events', JSON.stringify(existingEvents.slice(0, 50)));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (needsSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="p-4 bg-red-500/20 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <Shield className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Security Setup Required</h1>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Two-factor authentication is required to access the admin dashboard. This protects sensitive system controls and intellectual property.
            </p>
          </motion.div>
          
          <TwoFactorAuth
            mode="setup"
            onSetupComplete={handleSetupComplete}
            onVerificationComplete={() => undefined}
          />
        </div>
      </div>
    );
  }

  if (!isAuthenticated || sessionExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="p-4 bg-blue-500/20 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <Shield className="w-10 h-10 text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Authentication</h1>
            <p className="text-slate-400">
              {sessionExpired ? 'Your session has expired. Please re-authenticate.' : 'Please verify your identity to access the admin dashboard.'}
            </p>
            
            {sessionExpired && (
              <div className="mt-4 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center gap-2 justify-center">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-yellow-300">Session expired for security</span>
                </div>
              </div>
            )}
          </motion.div>
          
          <TwoFactorAuth
            mode="verify"
            onSetupComplete={() => undefined}
            onVerificationComplete={handleVerificationComplete}
          />
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
